import { useMemo, useState } from "react";
import { API_BASE_URL } from "../../../config/apiBaseUrl";
import {
  mapAdminSellerSubmissionImagesToListing,
  updateAdminSellerSubmissionImage,
} from "../../../services/sellerSubmissionAdminApi";

function resolvePreviewUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL.replace(/\/api\/?$/, "")}${url.startsWith("/") ? url : `/${url}`}`;
}

function reviewStatusLabel(status) {
  switch (status) {
    case "approved":
      return "Aprobada";
    case "rejected":
      return "Rechazada";
    case "pending_review":
      return "Pendiente";
    default:
      return status || "—";
  }
}

function reviewStatusClass(status) {
  switch (status) {
    case "approved":
      return "lead-badge lead-badge--success";
    case "rejected":
      return "lead-badge lead-badge--danger";
    default:
      return "lead-badge";
  }
}

/**
 * @param {{
 *   submissionId: string,
 *   images?: object[],
 *   imageReview?: object|null,
 *   convertedListingId?: string|null,
 *   canManage?: boolean,
 *   onUpdated?: (submissionPatch: object) => void,
 * }} props
 */
export default function SellerSubmissionImageReviewSection({
  submissionId,
  images = [],
  imageReview = null,
  convertedListingId = null,
  canManage = false,
  onUpdated,
}) {
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState("");
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const sortedImages = useMemo(
    () => images.slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [images]
  );

  const runAction = async (imageId, action, extra = {}) => {
    if (!canManage) return;
    setBusyId(imageId);
    setMessage("");
    try {
      const data = await updateAdminSellerSubmissionImage(submissionId, imageId, {
        action,
        ...extra,
      });
      onUpdated?.({
        images: data.images || images,
        imageReview: data.summary || imageReview,
      });
      setMessage("Imagen actualizada.");
      setRejectingId(null);
      setRejectReason("");
    } catch (err) {
      setMessage(err.message || "No pudimos actualizar la imagen.");
    } finally {
      setBusyId(null);
    }
  };

  const mapToListing = async () => {
    if (!canManage || !convertedListingId) return;
    setBusyId("map");
    setMessage("");
    try {
      const data = await mapAdminSellerSubmissionImagesToListing(submissionId, convertedListingId);
      onUpdated?.({
        images: data.images || images,
        imageReview: data.summary || imageReview,
      });
      setMessage(`Se mapearon ${data.mappedCount ?? 0} imágenes al borrador.`);
    } catch (err) {
      setMessage(err.message || "No pudimos mapear las imágenes.");
    } finally {
      setBusyId(null);
    }
  };

  if (!images.length) {
    return <p className="admin-table-subtext">No hay fotos enviadas por el vendedor.</p>;
  }

  return (
    <div className="seller-submission-image-review" data-testid="seller-submission-image-review">
      {imageReview ? (
        <p className="admin-table-subtext">
          Total: {imageReview.total} · Pendientes: {imageReview.pending} · Aprobadas: {imageReview.approved} ·
          Rechazadas: {imageReview.rejected}
          {imageReview.hasPrimary ? " · Principal seleccionada" : " · Sin principal"}
        </p>
      ) : null}

      <ul className="seller-submission-image-review__grid">
        {sortedImages.map((image) => (
          <li key={image.id} className="seller-submission-image-review__item">
            <a href={resolvePreviewUrl(image.previewUrl)} target="_blank" rel="noreferrer">
              <img src={resolvePreviewUrl(image.previewUrl)} alt="" />
            </a>
            <div className="seller-submission-image-review__meta">
              <span className={reviewStatusClass(image.reviewStatus)}>{reviewStatusLabel(image.reviewStatus)}</span>
              {image.isPrimary ? <span className="lead-badge lead-badge--success">Principal</span> : null}
              <p className="admin-table-subtext">{image.fileName}</p>
              <p className="admin-table-subtext">
                {image.width && image.height ? `${image.width}×${image.height}` : null}
                {image.fileSizeBytes ? ` · ${Math.round(image.fileSizeBytes / 1024)} KB` : null}
              </p>
              {image.rejectionReason ? (
                <p className="admin-table-subtext">Motivo: {image.rejectionReason}</p>
              ) : null}
            </div>
            {canManage ? (
              <div className="seller-submission-image-review__actions">
                {image.reviewStatus === "pending_review" ? (
                  <>
                    <button
                      type="button"
                      className="dealer-portal-btn dealer-portal-btn--primary"
                      disabled={busyId === image.id}
                      onClick={() => runAction(image.id, "approve")}
                    >
                      Aprobar
                    </button>
                    <button
                      type="button"
                      className="dealer-portal-btn"
                      disabled={busyId === image.id}
                      onClick={() => setRejectingId(image.id)}
                    >
                      Rechazar
                    </button>
                  </>
                ) : null}
                {image.reviewStatus === "approved" && !image.isPrimary ? (
                  <button
                    type="button"
                    className="dealer-portal-btn"
                    disabled={busyId === image.id}
                    onClick={() => runAction(image.id, "set_primary")}
                  >
                    Marcar principal
                  </button>
                ) : null}
              </div>
            ) : null}
            {rejectingId === image.id ? (
              <div className="seller-submission-image-review__reject">
                <label>
                  Motivo interno (opcional)
                  <textarea
                    rows={2}
                    value={rejectReason}
                    onChange={(event) => setRejectReason(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="dealer-portal-btn"
                  disabled={busyId === image.id}
                  onClick={() => runAction(image.id, "reject", { reason: rejectReason })}
                >
                  Confirmar rechazo
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {canManage && convertedListingId ? (
        <button
          type="button"
          className="dealer-portal-btn dealer-portal-btn--primary"
          disabled={busyId === "map"}
          onClick={mapToListing}
        >
          Mapear imágenes aprobadas al borrador
        </button>
      ) : null}

      {message ? <p className="admin-table-subtext">{message}</p> : null}
    </div>
  );
}
